/**
 * 要求レイヤーの書込入力の契約。
 *
 * 変更 1 件は「どの要求のためか」を持つ。要求は**既存を ID で指す**か、**その場で作る**かの
 * どちらかで、作る場合は why を 1 件以上、why は根拠を 1 件以上同梱する。
 *
 * **参照切れを構造で不可能にする（機構 1・2）。** 決定 s が ADR 参照を廃止した理由は
 * 「参照先の ADR が書かれる保証がないため、実在しない対象への制約になっていた」だった。
 * ここで参照先にするのは**その書込自身が作るデータ**である。先に作っておく工程が無いので、
 * 作り忘れによる参照切れが起こらない。既存を指した場合の実在確認は書込コアが行い、
 * 無ければ 1 行も書かずに拒否する。
 *
 * **制約 A・B は検査ではなく構造で満たす。**
 * - 制約 A（変更行の PJ と要求経由の PJ 集合が矛盾しないこと）: 要求を指した時点で、その
 *   PJ とその要求の紐付け行が必ず生まれる。矛盾する状態を作る経路が無い
 * - 制約 B（要求は why を 1 件以上持つこと）: 新規の要求は why 同梱でしか作れない。
 *   why を持たない要求を表現する入力が存在しない
 *
 * **文字数の下限は置かない。** `reason` 1 列だった頃の唯一の検査が 30 書記素で、それでも
 * 「PJ の要件に合わせるため対応した」が通っていた。長さは中身を保証しない。ここで効かせる
 * のは**構造**である — 実現すること（`statement`）と実現したと言える条件（`acceptance`）を
 * 分けて必須にし、困りごと（why の `statement`）と測り方（`outcome`）を分けて必須にし、
 * 原典・観測日時・観測値を持つ根拠を 1 件以上要求する。埋める欄が分かれていれば、1 文で
 * 全部を誤魔化す形が取れない。
 *
 * **上限は要求の文（`statement`）にだけ置く。** 下限と違って上限は構造の側から効く — 文が
 * 要旨の長さに収まらないのは、受け入れ条件と why に分けて書くはずの中身（条件・順序・例外）が
 * 文へ流れ込んだしるしである。長さで中身を保証するのではなく、欄の分け方を守らせるために置く。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 要求・why・根拠の書込入力の契約。既存を ID で指すか、その場で作るかの 2 形
 * @graph-connects none
 */
import { z } from 'zod';
import { nonEmptyString } from './node-schemas.js';
/**
 * 観測日時の形式。ISO 8601 の UTC 表記（秒未満は任意）。
 *
 * `baseRevision` と同じ形にしてある。秒未満の桁数を固定しないのは、精度が BQ クライアントの
 * 版で変わりうる一方、spec グラフ側にその桁数を決める権限が無いためである。ここが止めたいの
 * は `"先月"` や `"2026/9/14"` のような、そもそも時刻でない値である。
 * @graph-connects none
 */
const OBSERVED_AT_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?Z$/u;
/**
 * 根拠 1 件の入力。困りごとが実在することを示す観測である。
 *
 * **根拠を任意にすると「そう思うから」が通る。** ここが自由記述との最大の差なので、why は
 * これを 1 件以上持たなければ書き込めない。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 根拠 1 件の入力契約（原典・観測日時・観測値）
 * @graph-connects none
 */
export const evidenceInputSchema = z.strictObject({
    source: nonEmptyString(),
    observedAt: z
        .string()
        .regex(OBSERVED_AT_PATTERN, 'observedAt は観測した日時を ISO 8601 の UTC 表記で渡す（例: "2026-09-14T00:00:00.000Z"）。書込日時ではない'),
    value: nonEmptyString(),
});
/**
 * 要求の文（`statement`、「何を実現するか」）の上限の字数。
 *
 * 要求の文は画面で要求のカードの見出しになる。見出しが読めるのは要旨の長さまでで、条件・
 * 順序・例外まで 1 文に入れると（AIRCLOSET-139162 に約 90 字の文が 1 件あった）カードが文で
 * 埋まり、どの要求かを見分けられなくなる。同じ案件のほかの文は 13〜22 字で、読める長さに
 * 収まっている。30 はその全部を通し、長い 1 件だけを止める値である。
 *
 * **上限の値の宣言はここだけである。** 書込の検査（`requirementStatementString`）・拒否の文言と
 * MCP の案内（`REQUIREMENT_STATEMENT_FORMAT`）・画面の見出しの切り詰め
 * （`truncateRequirementStatement`）はどれもこの値から組む。入口に数値を写すと、ここを変えた
 * 日に入口ごとに上限が食い違う。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 要求の文の上限の字数（書記素で数える）。要旨より長い文を書込の段で止め、画面の見出しもこの字数で切る
 * @graph-connects none
 */
export const REQUIREMENT_STATEMENT_MAX_LENGTH = 30;
/**
 * 要求の文の形を人へ説明する 1 行。拒否の文言と MCP の案内で同じ文言を使う。
 * @graph-connects none
 */
export const REQUIREMENT_STATEMENT_FORMAT = `要求の文は要旨を ${String(REQUIREMENT_STATEMENT_MAX_LENGTH)} 字以内で書く。条件・順序・例外は受け入れ条件（acceptance）か why に書く`;
/**
 * 字数を**人が数える単位**（書記素クラスタ）で数える分割器。
 *
 * 変更理由の最低文字数（`SPEC_CHANGE_REASON_MIN_LENGTH`）が数えていた単位に揃える。
 * `String.prototype.length` は UTF-16 の符号単位数なので絵文字や結合文字を含む文が見た目より
 * 長く数えられ、コードポイント数でも結合文字は分かれる。要求の文は人が書いて人が読むので、
 * 人が数える単位に合わせる。生成は 1 度だけにする（書込 1 回ごとに生成すると積み上がる）。
 * @graph-connects none
 */
const GRAPHEME_SEGMENTER = new Intl.Segmenter('ja', { granularity: 'grapheme' });
/**
 * 要求の文を書記素に分ける。前後の空白は数えない。
 * @graph-connects none
 */
function statementGraphemes(statement) {
    return Array.from(GRAPHEME_SEGMENTER.segment(statement.trim()), ({ segment }) => segment);
}
/**
 * 要求の文の字数。書込の検査と画面の切り詰めが同じ数え方をするための唯一の口である。
 *
 * @param statement 要求の文
 * @returns 前後の空白を除いた書記素の数
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 要求の文の字数を書記素で数える（書込の上限の検査と画面の切り詰めが共有する数え方）
 * @graph-connects statementGraphemes [calls] 書記素への分割
 */
export function requirementStatementLength(statement) {
    return statementGraphemes(statement).length;
}
/**
 * 要求の文を見出しに載る長さへ切り詰める。上限以下の文は**そのまま返す**（前後の空白も触らない）。
 *
 * 既にある文は上限より長いことがある（上限は書込だけに効き、読取は長い文も返す）。見出しでは
 * 先頭を上限の字数で切って「…」を付け、全文は見出しの外（背景のモーダル等）で読ませる。
 *
 * @param statement 要求の文
 * @param limit 切る字数。省けば要求の文の上限
 * @returns 上限を超えれば先頭 `limit` 字と「…」、超えなければ元の文
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 要求の文を見出しに載る長さへ書記素単位で切り詰める（上限以下の文は変えない）
 * @graph-connects statementGraphemes [calls] 書記素への分割
 */
export function truncateRequirementStatement(statement, limit = REQUIREMENT_STATEMENT_MAX_LENGTH) {
    const graphemes = statementGraphemes(statement);
    return graphemes.length > limit ? `${graphemes.slice(0, limit).join('')}…` : statement;
}
/**
 * 要求の文のフィールド。非空で、上限の字数以内。**書込の入力にだけ使う**（読取の行スキーマには
 * 使わない。上限より前に書かれた長い文も読めなければならない）。
 *
 * 拒否の文言には今の字数を添える。「何字削ればよいか」が分からないと、書き手は要旨へ縮める
 * かわりに言い回しを少しずつ削って試し打ちする。
 *
 * @returns 要求の文の zod スキーマ
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 要求の文の入力スキーマ（非空・上限の字数以内）。要旨より長い文を書込の段で止める
 * @graph-connects requirementStatementLength [calls] 書記素で数えた字数
 */
export function requirementStatementString() {
    return nonEmptyString().superRefine((statement, context) => {
        const length = requirementStatementLength(statement);
        if (length <= REQUIREMENT_STATEMENT_MAX_LENGTH)
            return;
        context.addIssue({
            code: 'custom',
            message: `${REQUIREMENT_STATEMENT_FORMAT}（いまは ${String(length)} 字）`,
        });
    });
}
/**
 * 1 回の紐付けで指せる変更行の上限。
 *
 * 実データで最も大きい要求（AIRCLOSET-139162 の「マッチ度の概念を入れる」）は 281 行に触れる。
 * 1 桁多い上限を置くのは、上限そのものより「PJ の全変更をまとめて 1 つの要求に結ぶ」ような
 * 取り違えを入力の段で止めるためである。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 1 回の紐付けで要求に結べる変更行の上限
 * @graph-connects none
 */
export const LINK_REQUIREMENT_CHANGE_LIMIT = 1000;
//# sourceMappingURL=requirement-input.js.map